import React, { useState } from "react";
import { api } from "@/lib/api";
import { setQuickDevice, deviceLabel, markSetupDismissed } from "@/lib/quickAccess";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Fingerprint, ShieldCheck, Loader2 } from "lucide-react";

/**
 * Modal ofrecido justo después del primer login exitoso de un admin (sección 9.10 PRD).
 * Puede saltarse. Si el usuario acepta, registra un dispositivo confiado con PIN 4-6 dígitos.
 */
export function QuickAccessSetupDialog({ open, onOpenChange, onDone, firstName }) {
  const [step, setStep] = useState("intro"); // intro | pin | confirm
  const [pin1, setPin1] = useState("");
  const [pin2, setPin2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setStep("intro"); setPin1(""); setPin2(""); setSubmitting(false); };

  const dismiss = () => {
    markSetupDismissed();
    reset();
    onOpenChange(false);
  };

  const goNext = () => {
    if (step === "intro") setStep("pin");
    else if (step === "pin") {
      if (!/^\d{4,6}$/.test(pin1)) return toast.error("El PIN debe ser de 4 a 6 dígitos");
      setStep("confirm");
    }
  };

  const submit = async () => {
    if (pin1 !== pin2) return toast.error("Los PIN no coinciden");
    setSubmitting(true);
    try {
      const r = await api.post("/quick-access/register", { pin: pin1, device_label: deviceLabel() });
      setQuickDevice(r.data.device_id, r.data.first_name);
      toast.success("Acceso rápido activado en este dispositivo");
      reset();
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo activar acceso rápido");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent data-testid="qa-setup-dialog" className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto h-14 w-14 rounded-full bg-[#005A91]/10 flex items-center justify-center mb-2">
            <Fingerprint className="h-7 w-7 text-[#005A91]" />
          </div>
          <DialogTitle className="text-center text-2xl font-black text-[#005A91]">
            {step === "intro" && "¿Activar acceso rápido?"}
            {step === "pin" && "Crea tu PIN"}
            {step === "confirm" && "Confirma tu PIN"}
          </DialogTitle>
          <DialogDescription className="text-center pt-1">
            {step === "intro" && (
              <>La próxima vez, {firstName ? `${firstName}, ` : ""}entras con un <b>PIN de 4 a 6 dígitos</b> en lugar de tu contraseña. La contraseña seguirá siendo obligatoria en dispositivos nuevos o cada 7 días.</>
            )}
            {step === "pin" && "Escribe 4 a 6 dígitos numéricos. Se guarda encriptado y solo funciona en este dispositivo."}
            {step === "confirm" && "Repite el mismo PIN para confirmar."}
          </DialogDescription>
        </DialogHeader>

        {step === "intro" && (
          <div className="bg-slate-50 rounded-md p-4 text-xs text-slate-600 space-y-2 border border-slate-200">
            <div className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-[#4ECCC8] shrink-0 mt-0.5" /> <span>Solo para el rol Administrador. Colaboradores y técnicos siguen entrando con contraseña.</span></div>
            <div className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-[#4ECCC8] shrink-0 mt-0.5" /> <span>5 intentos fallidos → PIN bloqueado, se pide contraseña completa.</span></div>
            <div className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-[#4ECCC8] shrink-0 mt-0.5" /> <span>Puedes revocar el dispositivo desde <b>Mi cuenta</b> si lo pierdes.</span></div>
          </div>
        )}

        {(step === "pin" || step === "confirm") && (
          <div className="py-2">
            <input
              autoFocus
              data-testid={step === "pin" ? "qa-pin-input" : "qa-pin-confirm-input"}
              type="password"
              inputMode="numeric"
              pattern="\d*"
              maxLength={6}
              value={step === "pin" ? pin1 : pin2}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                if (step === "pin") setPin1(v); else setPin2(v);
              }}
              onKeyDown={(e) => { if (e.key === "Enter") step === "pin" ? goNext() : submit(); }}
              className="w-full text-center text-4xl tracking-[0.5em] font-black py-4 rounded-md border-2 border-[#005A91]/20 focus:outline-none focus:border-[#005A91] pe-numeric"
              placeholder="••••"
            />
            <div className="text-center text-xs text-slate-500 mt-2">
              {(step === "pin" ? pin1 : pin2).length}/6 dígitos
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
          <Button variant="outline" onClick={dismiss} data-testid="qa-setup-skip"
                  className="w-full sm:w-auto">
            {step === "intro" ? "Ahora no" : "Cancelar"}
          </Button>
          {step === "intro" && (
            <Button onClick={goNext} data-testid="qa-setup-start"
                    className="w-full sm:w-auto bg-[#2E75B5] hover:bg-[#3a88cd] text-white font-bold">
              Activar acceso rápido
            </Button>
          )}
          {step === "pin" && (
            <Button onClick={goNext} data-testid="qa-setup-next"
                    disabled={!/^\d{4,6}$/.test(pin1)}
                    className="w-full sm:w-auto bg-[#2E75B5] hover:bg-[#3a88cd] text-white font-bold">
              Continuar
            </Button>
          )}
          {step === "confirm" && (
            <Button onClick={submit} data-testid="qa-setup-confirm"
                    disabled={submitting || pin2.length < 4}
                    className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Guardar PIN
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default QuickAccessSetupDialog;
